// Small pure helpers for the booking stepper. Moved unchanged from Bookings.jsx so the
// stepper and the page it replaces behave identically.

// Backend sometimes stores missing values as the literal string "null"
// (e.g. orders synced in from Utilstop) instead of a real null/empty value -
// treat those the same as empty so they don't render as "null" or fail validation.
export const cleanBackendValue = (v) =>
  v === null || v === undefined || String(v).trim().toLowerCase() === "null" ? "" : v;

// Human-readable names for the fields the validation schema guards, so a failed
// submit names the field the way the form labels it.
export const FIELD_LABELS = {
  partner: "Profile",
  phoneNumber: "Mobile Number",
  userName: "Customer name",
  house_no: "Street / House no.",
  area: "Area",
  location: "Address",
  state: "State",
  country: "Country",
  email: "Email",
  lat: "Latitude",
  lng: "Longitude"
};

// Which step each validated field lives on - used to jump to the step that holds the first
// error when a submit fails. "Profile" (partner) is chosen on the Service step.
export const STEP_KEYS = ["customer", "service", "schedule", "review"];
export const FIELD_STEP = {
  phoneNumber: "customer",
  org_id: "customer",
  userName: "customer",
  email: "customer",
  house_no: "customer",
  area: "customer",
  location: "customer",
  state: "customer",
  landmark: "customer",
  country: "customer",
  lat: "customer",
  lng: "customer",
  partner: "service",
  status: "review",
  payment: "review"
};
// Formik fields checked when leaving each step.
export const STEP_FIELDS = {
  customer: ["phoneNumber", "org_id", "userName", "email", "house_no", "area", "location", "state", "landmark", "country", "lat", "lng"],
  service: ["partner"],
  schedule: [],
  review: ["status", "payment"]
};

export const stepOfField = (field) => FIELD_STEP[field] || "customer";

export const formatPhoneNumber = (phone, currency) => {
  if (!phone) return "";

  let cleaned = phone.toString().replace(/\D/g, "");

  if (currency === "INR") {
    // Remove India country code if already present
    if (cleaned.startsWith("91") && cleaned.length > 10) {
      cleaned = cleaned.slice(2);
    }
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }

  if (currency === "AED") {
    // Remove UAE country code if already present
    if (cleaned.startsWith("971") && cleaned.length > 9) {
      cleaned = cleaned.slice(3);
    }
    return `+971 ${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
  }

  return phone;
};

export const convertTo12Hour = (time24) => {
  if (!time24) return "";
  const [hourStr, minute] = time24?.split(":");
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12; // Convert 0 to 12
  return `${hour}:${minute} ${ampm}`;
};

export const formatDateDisplay = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

// 'YYYY-MM-DD' of a Date in the browser's local time (the format slots and the API use).
export const dateToYmd = (date) => {
  const d1 = date.toLocaleDateString("en-GB").split(/[/\-.]/);
  return `${d1[2]}-${d1[1]}-${d1[0]}`;
};

export const todayYmd = () => {
  const d1 = new Date().toLocaleDateString("en-GB").split(/[/\-.]/);
  return `${d1[2]}-${d1[1]}-${d1[0]}`;
};

// True when the booking date (a Date straight from the date picker, or a 'YYYY-MM-DD' string) is
// before today - a backdated / backfilled booking.
export const isPastDate = (date) => {
  if (!date) return false;
  const ymd = date instanceof Date ? (Number.isNaN(date.getTime()) ? "" : dateToYmd(date)) : String(date).split("T")[0];
  return !!ymd && ymd < todayYmd();
};

// Industry wording, inline per component convention (not a central label map): the
// crew/provider noun used by the old booking form.
export const crewNoun = ({ isSportsService, isCarService, isSalonService }) =>
  isSportsService ? "Sports" : isCarService || isSalonService ? "Service Provider" : "Crew";

export const resourceNoun = ({ isSportsService, isCarService, isSalonService }) =>
  isSportsService ? "Court / Field" : isCarService ? "Bay / Equipment" : isSalonService ? "Chair / Station" : "Resource";
